/**
 * Pre-built layout templates that users can choose from
 * Each template follows the V2 layout format
 */

// Memorial box constants - minimum size is same as initial/default size
// 15% thinner: 460 * 0.85 = 391
const MEMORIAL_BOX_MIN_WIDTH = 391; // 15% thinner than original
const MEMORIAL_BOX_MIN_HEIGHT = 170; // Same as initial height

// Helper function to create memorial boxes with custom styling
// Display content area: 1920x960 (minus header 84px + footer 36px from 1080px)
export const createMemorialBoxes = (customStyling = {}) => [
  {
    id: 'memorial_ilui_nishmat',
    displayName: 'לעילוי נשמת',
    position: customStyling.position1 || { x: 540, y: 730, width: 391, height: 170 },
    styling: {
      showBorder: true,
      borderColor: customStyling.borderColor || '#d4af37',
      backgroundColor: customStyling.backgroundColor || 'rgba(0,0,0,0.3)',
      titleFont: 'Arial',
      titleColor: customStyling.titleColor || '#ffc764',
      titleSize: 20,
      textFont: 'Arial',
      textColor: '#ffffff',
      textSize: 18,
      highlightEnabled: false
    },
    items: [], // Data comes from shul.ilui_nishmat
    showOnDisplay: true,
    isMemorialBox: true,
    memorialType: 'ilui',
    minWidth: MEMORIAL_BOX_MIN_WIDTH,
    minHeight: MEMORIAL_BOX_MIN_HEIGHT
  },
  {
    id: 'memorial_refuah_shleima',
    displayName: 'רפואה שלמה',
    position: customStyling.position2 || { x: 970, y: 730, width: 391, height: 170 },
    styling: {
      showBorder: true,
      borderColor: customStyling.borderColor || '#d4af37',
      backgroundColor: customStyling.backgroundColor || 'rgba(0,0,0,0.3)',
      titleFont: 'Arial',
      titleColor: customStyling.titleColor || '#ffc764',
      titleSize: 20,
      textFont: 'Arial',
      textColor: '#ffffff',
      textSize: 18,
      highlightEnabled: false
    },
    items: [], // Data comes from shul.refuah_shleima
    showOnDisplay: true,
    isMemorialBox: true,
    memorialType: 'refuah',
    minWidth: MEMORIAL_BOX_MIN_WIDTH,
    minHeight: MEMORIAL_BOX_MIN_HEIGHT
  }
];

export const LAYOUT_TEMPLATES = [
  {
    id: 'classic-five-box',
    name: 'Classic Five Box',
    description: 'Traditional layout with Shabbos, Weekday, Learning, Calendar, and Announcements',
    thumbnail: '📺',
    layout: {
      version: 'v2',
      boxes: [
        // Box 1: Shabbos Times (Left side, tall)
        {
          id: 'box_shabbos',
          displayName: 'Shabbos Times',
          position: { x: 40, y: 20, width: 420, height: 550 },
          styling: {
            showBorder: true,
            borderColor: '#d4af37',
            backgroundColor: 'transparent',
            titleFont: 'Arial',
            titleColor: '#ffc764',
            titleSize: 24,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 22,
            highlightEnabled: true,
            highlightType: 'glow',
            highlightColor: '#00ff00'
          },
          items: [],
          showOnDisplay: true
        },
        // Box 2: Weekday Times (Right side, tall)
        {
          id: 'box_weekday',
          displayName: 'Weekday Times',
          position: { x: 1460, y: 20, width: 420, height: 550 },
          styling: {
            showBorder: true,
            borderColor: '#d4af37',
            backgroundColor: 'transparent',
            titleFont: 'Arial',
            titleColor: '#ffc764',
            titleSize: 24,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 22,
            highlightEnabled: true,
            highlightType: 'glow',
            highlightColor: '#00ff00'
          },
          items: [],
          showOnDisplay: true
        },
        // Box 3: Daily Learning (Bottom left)
        {
          id: 'box_learning',
          displayName: 'Daily Learning',
          position: { x: 40, y: 590, width: 420, height: 310 },
          styling: {
            showBorder: true,
            borderColor: '#d4af37',
            backgroundColor: 'transparent',
            titleFont: 'Arial',
            titleColor: '#ffc764',
            titleSize: 20,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 18,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Box 4: Jewish Calendar (Bottom right)
        {
          id: 'box_calendar',
          displayName: 'Jewish Calendar',
          position: { x: 1460, y: 590, width: 420, height: 310 },
          styling: {
            showBorder: true,
            borderColor: '#d4af37',
            backgroundColor: 'transparent',
            titleFont: 'Arial',
            titleColor: '#ffc764',
            titleSize: 20,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 18,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Box 5: Announcements (Top center)
        {
          id: 'box_announcements',
          displayName: 'Announcements',
          position: { x: 490, y: 20, width: 950, height: 140 },
          styling: {
            showBorder: true,
            borderColor: '#d4af37',
            backgroundColor: 'transparent',
            titleFont: 'Arial',
            titleColor: '#ffc764',
            titleSize: 20,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 24,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Box 6: Logo (Center)
        {
          id: 'box_logo',
          displayName: 'Logo',
          position: { x: 685, y: 200, width: 550, height: 360 },
          styling: {
            showBorder: true,
            borderColor: '#d4af37',
            backgroundColor: 'transparent',
            titleFont: 'Arial',
            titleColor: '#ffc764',
            titleSize: 20,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 18,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Memorial boxes (integrated into classic design at center bottom)
        ...createMemorialBoxes({
          position1: { x: 505, y: 730, width: 440, height: 170 },
          position2: { x: 975, y: 730, width: 440, height: 170 },
          borderColor: '#d4af37',
          backgroundColor: 'rgba(0,0,0,0.5)',
          titleColor: '#ffc764'
        })
      ],
      globalSettings: {
        backgroundType: 'default',
        backgroundColor: '#000000',
        backgroundImage: null,
        headerBgColor: '#162A45',
        headerTextColor: '#ffc764',
        boxesOutlineColor: '#d4af37',
        boxesBackgroundColor: 'transparent'
      }
    }
  },
  {
    id: 'simple-three-column',
    name: 'Simple Three Column',
    description: 'Clean three-column layout for essential information',
    thumbnail: '📊',
    layout: {
      version: 'v2',
      boxes: [
        // Left Column: Shabbos Zmanim
        {
          id: 'box_shabbos',
          displayName: 'Shabbos Zmanim',
          position: { x: 0, y: 20, width: 480, height: 900 },
          styling: {
            showBorder: true,
            borderColor: '#3498db',
            backgroundColor: 'rgba(0,0,0,0.3)',
            titleFont: 'Arial',
            titleColor: '#3498db',
            titleSize: 26,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 22,
            highlightEnabled: true,
            highlightType: 'color',
            highlightColor: '#e74c3c'
          },
          items: [],
          showOnDisplay: true
        },
        // Center Top: Logo
        {
          id: 'box_logo',
          displayName: 'Logo',
          position: { x: 500, y: 20, width: 880, height: 360 },
          styling: {
            showBorder: true,
            borderColor: '#3498db',
            backgroundColor: 'rgba(0,0,0,0.3)',
            titleFont: 'Arial',
            titleColor: '#3498db',
            titleSize: 26,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 22,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Center Middle: Limudim
        {
          id: 'box_learning',
          displayName: 'Limudim',
          position: { x: 500, y: 400, width: 880, height: 330 },
          styling: {
            showBorder: true,
            borderColor: '#3498db',
            backgroundColor: 'rgba(0,0,0,0.3)',
            titleFont: 'Arial',
            titleColor: '#3498db',
            titleSize: 26,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 22,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Right Column: Weekday Zmanim
        {
          id: 'box_weekday',
          displayName: 'Weekday Zmanim',
          position: { x: 1400, y: 20, width: 480, height: 900 },
          styling: {
            showBorder: true,
            borderColor: '#3498db',
            backgroundColor: 'rgba(0,0,0,0.3)',
            titleFont: 'Arial',
            titleColor: '#3498db',
            titleSize: 26,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 24,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Memorial boxes (side by side center bottom, matching blue theme)
        ...createMemorialBoxes({
          position1: { x: 533, y: 750, width: 391, height: 170 },
          position2: { x: 956, y: 750, width: 391, height: 170 },
          borderColor: '#3498db',
          backgroundColor: 'rgba(52, 152, 219, 0.15)',
          titleColor: '#3498db'
        })
      ],
      globalSettings: {
        backgroundType: 'color',
        backgroundColor: '#1a1a2e',
        backgroundImage: null,
        headerBgColor: '#16213e',
        headerTextColor: '#3498db',
        boxesOutlineColor: '#3498db',
        boxesBackgroundColor: 'rgba(0,0,0,0.3)'
      }
    }
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Sleek modern design with focus on key times',
    thumbnail: '✨',
    layout: {
      version: 'v2',
      boxes: [
        // Left: Large Prayer Times
        {
          id: 'box_main_times',
          displayName: 'Prayer Times',
          position: { x: 40, y: 20, width: 880, height: 710 },
          styling: {
            showBorder: true,
            borderColor: '#95a5a6',
            backgroundColor: 'rgba(255,255,255,0.05)',
            titleFont: 'Arial',
            titleColor: '#ecf0f1',
            titleSize: 32,
            textFont: 'Arial',
            textColor: '#ecf0f1',
            textSize: 28,
            highlightEnabled: true,
            highlightType: 'glow',
            highlightColor: '#27ae60'
          },
          items: [],
          showOnDisplay: true
        },
        // Right Top: Announcements
        {
          id: 'box_announcements',
          displayName: 'Announcements',
          position: { x: 960, y: 20, width: 920, height: 330 },
          styling: {
            showBorder: true,
            borderColor: '#95a5a6',
            backgroundColor: 'rgba(255,255,255,0.05)',
            titleFont: 'Arial',
            titleColor: '#ecf0f1',
            titleSize: 24,
            textFont: 'Arial',
            textColor: '#ecf0f1',
            textSize: 24,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Bottom Right Left: Learning
        {
          id: 'box_learning',
          displayName: 'Daily Learning',
          position: { x: 960, y: 370, width: 440, height: 360 },
          styling: {
            showBorder: true,
            borderColor: '#95a5a6',
            backgroundColor: 'rgba(255,255,255,0.05)',
            titleFont: 'Arial',
            titleColor: '#ecf0f1',
            titleSize: 22,
            textFont: 'Arial',
            textColor: '#ecf0f1',
            textSize: 20,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Bottom Right Right: Calendar
        {
          id: 'box_calendar',
          displayName: 'Hebrew Date',
          position: { x: 1440, y: 370, width: 440, height: 360 },
          styling: {
            showBorder: true,
            borderColor: '#95a5a6',
            backgroundColor: 'rgba(255,255,255,0.05)',
            titleFont: 'Arial',
            titleColor: '#ecf0f1',
            titleSize: 22,
            textFont: 'Arial',
            textColor: '#ecf0f1',
            textSize: 20,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Memorial boxes (side by side bottom left, matching gray theme)
        ...createMemorialBoxes({
          position1: { x: 40, y: 750, width: 391, height: 170 },
          position2: { x: 471, y: 750, width: 391, height: 170 },
          borderColor: '#95a5a6',
          backgroundColor: 'rgba(255,255,255,0.08)',
          titleColor: '#ecf0f1'
        })
      ],
      globalSettings: {
        backgroundType: 'color',
        backgroundColor: '#2c3e50',
        backgroundImage: null,
        headerBgColor: '#34495e',
        headerTextColor: '#ecf0f1',
        boxesOutlineColor: '#95a5a6',
        boxesBackgroundColor: 'rgba(255,255,255,0.05)'
      }
    }
  },
  {
    id: 'shabbos-focused',
    name: 'Shabbos Focused',
    description: 'Large central box for Shabbos times with parsha display',
    thumbnail: '🕯️',
    layout: {
      version: 'v2',
      boxes: [
        // Large center/left box for Shabbos
        {
          id: 'box_shabbos',
          displayName: 'Shabbos Kodesh',
          position: { x: 40, y: 20, width: 900, height: 710 },
          styling: {
            showBorder: true,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(0,0,0,0.4)',
            titleFont: 'Arial',
            titleColor: '#f39c12',
            titleSize: 36,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 32,
            highlightEnabled: true,
            highlightType: 'glow',
            highlightColor: '#f39c12'
          },
          items: [],
          showOnDisplay: true
        },
        // Right Top: Weekday morning
        {
          id: 'box_morning',
          displayName: 'Weekday Times',
          position: { x: 980, y: 20, width: 900, height: 360 },
          styling: {
            showBorder: true,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(0,0,0,0.4)',
            titleFont: 'Arial',
            titleColor: '#f39c12',
            titleSize: 24,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 20,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Right Bottom: Learning & Calendar
        {
          id: 'box_learning',
          displayName: 'Daily Learning & Calendar',
          position: { x: 980, y: 400, width: 900, height: 330 },
          styling: {
            showBorder: true,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(0,0,0,0.4)',
            titleFont: 'Arial',
            titleColor: '#f39c12',
            titleSize: 24,
            textFont: 'Arial',
            textColor: '#ffffff',
            textSize: 20,
            highlightEnabled: false
          },
          items: [],
          showOnDisplay: true
        },
        // Memorial boxes (center bottom, matching gold/orange theme)
        ...createMemorialBoxes({
          position1: { x: 489, y: 750, width: 391, height: 170 },
          position2: { x: 920, y: 750, width: 391, height: 170 },
          borderColor: '#f39c12',
          backgroundColor: 'rgba(243, 156, 18, 0.15)',
          titleColor: '#f39c12'
        })
      ],
      globalSettings: {
        backgroundType: 'default',
        backgroundColor: '#000000',
        backgroundImage: null,
        headerBgColor: '#8B4513',
        headerTextColor: '#f39c12',
        boxesOutlineColor: '#f39c12',
        boxesBackgroundColor: 'rgba(0,0,0,0.4)'
      }
    }
  }
];

export default LAYOUT_TEMPLATES;
